import { createReducer, on } from '@ngrx/store';
import {
  changeCartItemQuantity,
  clearCart,
  decrementCartItem,
  deleteCartItem,
  incrementCartItem,
  loadCartData,
  addItemToCartFnReducer,
} from './cart.state';
import { PharmacistCartDetailsDTO } from '../../pages/user-dashboard/user-cart/user-cart.model';
import {
  AddCartBundleDTO,
  AddCartOfferDTO,
  AddCartProductDTO,
  CartDto,
} from '../../pages/admin-dashboard/cart-management/admin-cart.model';

const initialState: PharmacistCartDetailsDTO | {} = {};

const round2 = (n: number) => Math.round((n + Number.EPSILON) * 100) / 100;

const getItemQuantity = (i: any): number => +(i?.item?.quantity ?? i?.quantity ?? 0);

// Update quantity wherever it lives (nested item or top level)
const setItemQuantity = (i: any, qty: number) =>
  i?.item?.quantity !== undefined
    ? { ...i, quantity: qty.toString(), item: { ...i.item, quantity: qty } }
    : { ...i, quantity: qty.toString() };

const lineTotals = (items: any[]) =>
  items.reduce(
    (acc, i) => {
      const product = i?.item?.customerProduct ?? i?.item;
      const qty = getItemQuantity(i);
      acc.business += +(product?.businessPrice ?? 0) * qty;
      acc.customer += +(product?.customerPrice ?? 0) * qty;
      acc.qty += qty;
      return acc;
    },
    { business: 0, customer: 0, qty: 0 },
  );

// Rebuild all totals from the new items, keeping the current discount ratio
const recalcCart = (state: any, items: any[]) => {
  const totals = lineTotals(items);
  const ratio = +state.totalBusinessPrice > 0 ? +state.finalBusinessPrice / +state.totalBusinessPrice : 1;

  return {
    ...state,
    items,
    totalBusinessPrice: round2(totals.business),
    finalBusinessPrice: round2(Math.max(0, totals.business * ratio)),
    finalCustomerPrice: round2(totals.customer),
    totalQuantity: totals.qty.toString(),
  };
};
const cartReducer = createReducer(
  initialState,
  on(loadCartData, (_, { data }) => {
    console.log('Loading cart data into state:', data);
    return data;
  }),
  on(incrementCartItem, (state: any, { cartItemId }) => {
    if (!state?.items) return state;
    const items = state.items.map((i: any) => (i.id === cartItemId ? setItemQuantity(i, getItemQuantity(i) + 1) : i));
    return recalcCart(state, items);
  }),

  on(decrementCartItem, (state: any, { cartItemId }) => {
    if (!state?.items) return state;
    const items = state.items.map((i: any) =>
      i.id === cartItemId ? setItemQuantity(i, Math.max(1, getItemQuantity(i) - 1)) : i,
    );
    return recalcCart(state, items);
  }),

  on(changeCartItemQuantity, (state: any, { cartItemId, quantity }) => {
    if (!state?.items) return state;
    if (!state.items.some((i: any) => i.id === cartItemId)) return state;
    const items = state.items.map((i: any) => (i.id === cartItemId ? setItemQuantity(i, Math.max(1, +quantity)) : i));
    return recalcCart(state, items);
  }),

  on(deleteCartItem, (state: any, { cartItemId }) => {
    if (!state?.items) return state;
    const items = state.items.filter((i: any) => i.id !== cartItemId);
    return recalcCart(state, items);
  }),
  on(clearCart, (state: any) => {
    return {
      ...state,
      items: [],
      totalBusinessPrice: 0,
      finalBusinessPrice: 0,
      finalCustomerPrice: 0,
      totalQuantity: String(0),
    };
  }),

  on(
    addItemToCartFnReducer,
    (state: any, { itemType, pharmacyId, dto }: { itemType: any; pharmacyId: any; dto: CartDto }) => {
      const existingItems = state?.items ?? [];

      const getKey = (entry: AddCartProductDTO | AddCartOfferDTO | AddCartBundleDTO) => {
        switch (entry.type) {
          case 'product':
            return `product-${entry.customerProductId}`;
          case 'offer':
            return `offer-${entry.offerId}`;
          case 'bundle':
            return `bundle-${entry.bundleId}`;
        }
      };

      // How much quantity a single incoming entry actually contributes
      const getQuantity = (entry: AddCartProductDTO | AddCartOfferDTO | AddCartBundleDTO): number => {
        if (entry.type === 'offer') {
          return entry.products.reduce((sum, p) => sum + p.quantity, 0);
        }
        return entry.quantity; // product | bundle
      };

      let newItems = [...existingItems];
      let addedQuantity = 0;

      for (const incoming of dto.items) {
        addedQuantity += getQuantity(incoming);

        const key = getKey(incoming);
        const existingIndex = newItems.findIndex((i: any) => getKey(i) === key);

        if (existingIndex > -1) {
          newItems = newItems.map((i: any, idx: number) => {
            if (idx !== existingIndex) return i;
            if (incoming.type === 'offer') {
              return { ...i, ...incoming };
            }
            return { ...i, quantity: (+i.quantity + incoming.quantity).toString() };
          });
        } else {
          newItems.push({ ...incoming, pharmacyId });
        }
      }

      return {
        ...state,
        pharmacyId,
        items: newItems,
        totalQuantity: (+state.totalQuantity + addedQuantity).toString(),
      };
    },
  ),
);

export { cartReducer };
